import { useState } from "react";
import { format } from "date-fns";
import { CalendarX, Info } from "lucide-react";
import { parseLocalDate } from "@/lib/utils";
import { FullDateDto } from "@/types/fullDate";

/** How many dates to show before collapsing the rest behind a toggle. */
const COLLAPSE_AFTER = 6;

/**
 * The calendar date a picked Date falls on, as the customer sees it in the
 * picker. Formatting the local parts rather than calling toISOString() matters:
 * toISOString() converts to UTC first, so an evening selection would be reported
 * as the following day.
 */
export function toCalendarDate(date: Date): string {
  return format(date, "yyyy-MM-dd");
}

function formatForDisplay(isoDate: string): string {
  const parsed = parseLocalDate(isoDate);
  // Falls back to the raw value rather than throwing. parseLocalDate returns a
  // Date for anything non-empty, including an unparseable string, and format()
  // throws on an invalid one — which would take down the whole bid request form
  // over a cosmetic detail of a notice that is only advisory in the first place.
  if (!parsed || Number.isNaN(parsed.getTime())) {
    return isoDate;
  }
  return format(parsed, "EEE, MMM d");
}

/**
 * A heads up that some dates are already fully booked.
 *
 * Advisory only, by design. Nothing here disables a date, blocks the form or
 * gates the submit button: someone who asks for a full date anyway is a
 * motivated customer, not a mistake to be prevented. The point is only to nudge
 * the undecided towards a date we can actually staff.
 */
export const FullDatesNotice = ({
  fullDates,
}: {
  fullDates: FullDateDto[];
}) => {
  const [showAll, setShowAll] = useState(false);

  // No full dates means no notice at all, not an empty box saying so.
  if (fullDates.length === 0) {
    return null;
  }

  const visible = showAll ? fullDates : fullDates.slice(0, COLLAPSE_AFTER);
  const hiddenCount = fullDates.length - visible.length;

  return (
    <div className="rounded-lg border border-amber-500/40 bg-amber-50 p-4 dark:bg-amber-950/30">
      <div className="flex gap-3">
        <Info
          className="mt-0.5 h-4 w-4 shrink-0 text-amber-700 dark:text-amber-400"
          aria-hidden="true"
        />
        <div className="space-y-2 text-sm">
          <p className="font-medium text-amber-900 dark:text-amber-200">
            {fullDates.length === 1
              ? "One date is already fully booked"
              : "Some dates are already fully booked"}
          </p>
          <ul className="flex flex-wrap gap-x-4 gap-y-1 text-amber-900/90 dark:text-amber-200/90">
            {visible.map((fullDate) => (
              <li key={fullDate.date}>
                {formatForDisplay(fullDate.date)}
                {fullDate.note ? (
                  <span className="text-amber-900/70 dark:text-amber-200/70">
                    {" "}
                    — {fullDate.note}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
          {hiddenCount > 0 ? (
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className="text-amber-900 underline underline-offset-2 dark:text-amber-200"
            >
              Show {hiddenCount} more
            </button>
          ) : null}
          <p className="text-amber-900/80 dark:text-amber-200/80">
            You're welcome to request one of these anyway. If your date is at
            all flexible, though, another one gives us a much better chance of
            making it happen.
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * The same warning, aimed at the date the customer has actually picked. This is
 * the one that changes anything: the list above gets skimmed past, while this
 * appears directly under the picker once the choice is made.
 */
export const SelectedDateFullWarning = ({
  fullDates,
  selectedDate,
}: {
  fullDates: FullDateDto[];
  selectedDate?: Date | null;
}) => {
  if (!selectedDate || Number.isNaN(selectedDate.getTime())) {
    return null;
  }

  const picked = toCalendarDate(selectedDate);
  const match = fullDates.find((fullDate) => fullDate.date === picked);

  if (!match) {
    return null;
  }

  return (
    <div
      className="flex gap-2 text-sm text-amber-700 dark:text-amber-400"
      role="status"
    >
      <CalendarX className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <span>
        {formatForDisplay(match.date)} is already fully booked
        {/* Parenthesised rather than run on as its own sentence: a note is a
            fragment written by an admin, so it does not start with a capital. */}
        {match.note ? ` (${match.note})` : ""}. You can still submit this
        request, but another date is much more likely to get booked.
      </span>
    </div>
  );
};
